import {
  Box,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
} from "@mui/material";
import ApartmentIcon from "@mui/icons-material/Apartment";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { ClientTypes } from "../../features/auth/types/authType";
import { selectAuth } from "../../features/auth/authSlice";
import {
  selectPreference,
  setLocalAgency,
} from "../../features/preferences/preferencesSlice";

const ALL = "all";

type Props = {
  /** "large" renders a borderless, heading-style switcher for the title slot. */
  size?: "normal" | "large";
};

/**
 * Top-bar workspace switcher for Moderator users overseeing several agencies.
 * Reuses the global (persisted) `agencyId` filter: picking an agency scopes
 * every listing; the "all" option (labelled with the moderator's own name)
 * clears it back to the full group.
 */
export const AgencySwitcher = ({ size = "normal" }: Props) => {
  const dispatch = useAppDispatch();
  const { user } = useAppSelector(selectAuth);
  const { agencyId } = useAppSelector(selectPreference);

  const company = user?.company;
  const agencies = (company?.partners ?? []).filter(
    (c) => c.type === ClientTypes.LocalAgency,
  );

  if (company?.type !== ClientTypes.Moderator || agencies.length < 2) {
    return null;
  }

  // Guard against a stale persisted id that no longer matches an agency.
  const value = agencies.some((a) => String(a._id) === String(agencyId))
    ? (agencyId as string)
    : ALL;

  const handleChange = (e: SelectChangeEvent) => {
    const next = e.target.value;
    dispatch(setLocalAgency(next === ALL ? undefined : next));
  };

  const labelFor = (val: string) =>
    val === ALL
      ? company.displayName
      : agencies.find((a) => String(a._id) === String(val))?.displayName ??
        company.displayName;

  const large = size === "large";

  const chipSx = {
    color: "#fff",
    fontWeight: 600,
    borderRadius: 2,
    backgroundColor: "rgba(255, 255, 255, 0.12)",
    transition: "background-color 0.2s ease",
    "&:hover": { backgroundColor: "rgba(255, 255, 255, 0.2)" },
    ".MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255, 255, 255, 0.35)",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: "rgba(255, 255, 255, 0.7)",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#fff",
    },
    ".MuiSvgIcon-root": { color: "#fff" },
  };

  const headingSx = {
    color: "#fff",
    fontWeight: 900,
    fontFamily: "Open Sans",
    fontSize: { md: "2rem" },
    textTransform: "capitalize" as const,
    ".MuiOutlinedInput-notchedOutline": { border: "none" },
    "&:hover .MuiOutlinedInput-notchedOutline": { border: "none" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { border: "none" },
    ".MuiSelect-select": { pl: 0 },
    ".MuiSvgIcon-root": { color: "#fff" },
  };

  return (
    <FormControl
      size="small"
      sx={large ? { minWidth: 220 } : { mr: 1.5, minWidth: 200 }}
    >
      <Select
        value={value}
        onChange={handleChange}
        renderValue={(val) => (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ApartmentIcon fontSize={large ? "large" : "small"} />
            <Box
              component="span"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {labelFor(val)}
            </Box>
          </Box>
        )}
        MenuProps={{ PaperProps: { sx: { maxHeight: 360 } } }}
        sx={large ? headingSx : chipSx}
      >
        <MenuItem value={ALL}>{company.displayName}</MenuItem>
        {agencies.map((a) => (
          <MenuItem key={a._id} value={a._id}>
            {a.displayName}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
